import { useState, FormEvent } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router';
import { useDarkMode } from '../context/DarkModeContext';
import { api } from '../lib/api';
import { Eye, EyeOff, Check, ArrowLeft } from 'lucide-react';
import { Logo } from '../components/Logo';

export default function ResetPassword() {
  const { darkMode } = useDarkMode();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const validatePassword = (pw: string): string => {
    const missing: string[] = [];
    if (pw.length < 8) missing.push('mindestens 8 Zeichen');
    if (!/[a-z]/.test(pw)) missing.push('einen Kleinbuchstaben');
    if (!/[A-Z]/.test(pw)) missing.push('einen Großbuchstaben');
    if (!/\d/.test(pw)) missing.push('eine Zahl');
    if (missing.length === 0) return '';
    if (missing.length === 1) return `Es fehlt: ${missing[0]}`;
    return `Es fehlt: ${missing.slice(0, -1).join(', ')} und ${missing[missing.length - 1]}`;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setPasswordError('');

    if (!token) {
      setError('Kein gültiger Token vorhanden. Bitte fordern Sie einen neuen Link an.');
      return;
    }

    const pwError = validatePassword(password);
    if (pwError) {
      setPasswordError(pwError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/api/auth/reset-password', { token, password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err: any) {
      setError(err.message || 'Ein Fehler ist aufgetreten');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (hasError: boolean) => {
    const border = hasError
      ? 'border-red-500'
      : darkMode
        ? 'border-slate-600'
        : 'border-slate-300';
    return `w-full pl-4 pr-10 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${border} ${
      darkMode
        ? 'bg-slate-700 text-white placeholder-slate-400'
        : 'bg-white text-slate-900 placeholder-slate-400'
    }`;
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-4 py-12 ${
        darkMode ? 'bg-slate-900' : 'bg-slate-50'
      }`}
    >
      <div
        className={`w-full max-w-md rounded-2xl border shadow-lg p-8 ${
          darkMode
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-slate-200'
        }`}
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="mb-3">
            <Logo size="lg" />
          </div>
          <h1
            className={`text-2xl font-bold ${
              darkMode ? 'text-white' : 'text-slate-900'
            }`}
          >
            Neues Passwort
          </h1>
          <p
            className={`mt-1 text-sm text-center ${
              darkMode ? 'text-slate-400' : 'text-slate-500'
            }`}
          >
            Geben Sie Ihr neues Passwort ein
          </p>
        </div>

        {success ? (
          /* Success State */
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h2
              className={`text-lg font-semibold mb-2 ${
                darkMode ? 'text-white' : 'text-slate-900'
              }`}
            >
              Passwort zurückgesetzt
            </h2>
            <p
              className={`text-sm mb-6 ${
                darkMode ? 'text-slate-400' : 'text-slate-500'
              }`}
            >
              Ihr Passwort wurde erfolgreich geändert. Sie können sich jetzt mit
              Ihrem neuen Passwort anmelden.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors cursor-pointer"
            >
              Zur Anmeldung
            </Link>
          </div>
        ) : (
          /* Form */
          <>
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Hidden username field so browsers offer to save the new password */}
              <input type="email" autoComplete="username" className="hidden" readOnly />

              {/* New Password */}
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    darkMode ? 'text-slate-300' : 'text-slate-700'
                  }`}
                >
                  Neues Passwort
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError('');
                      setError('');
                    }}
                    placeholder="Mindestens 8 Zeichen"
                    autoComplete="new-password"
                    className={inputClass(!!passwordError)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${
                      darkMode ? 'text-slate-400' : 'text-slate-500'
                    }`}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {passwordError && (
                  <p className="mt-1 text-xs text-red-500">{passwordError}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    darkMode ? 'text-slate-300' : 'text-slate-700'
                  }`}
                >
                  Passwort bestätigen
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError('');
                  }}
                  placeholder="Passwort wiederholen"
                  autoComplete="new-password"
                  className={inputClass(false)}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                <span>{isSubmitting ? 'Wird gespeichert...' : 'Passwort zurücksetzen'}</span>
              </button>
            </form>

            <p
              className={`mt-6 text-center text-sm ${
                darkMode ? 'text-slate-400' : 'text-slate-500'
              }`}
            >
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium cursor-pointer"
              >
                <ArrowLeft size={14} />
                Zurück zur Anmeldung
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
